import { useLeadModal } from "./useLeadModal";
import { Text } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { LeadModalHeader } from "./LeadModalHeader";
import { ViewSubSection } from "shared/components/app";
import { EnumLeadStatus } from "gql/generated";
import { SurveyResponseModal } from "shared/components/organisms/SurveyResponseModal";

type LeadModalProps = {
  isOpen: boolean;
  onClose: () => void;
  leadId: string | null;
  onOpenBroadcastFromLead?: (broadcastId: string, resourceId: string) => void;
  portal?: boolean;
};

const ACCENT = {
  banner: "bg.accent.magenta.mid",
  closeBg: "bg.accent.magenta.dark",
  closeBgHover: "bg.accent.magenta.darkest",
};

export const LeadModal = ({
  isOpen,
  onClose,
  leadId,
  onOpenBroadcastFromLead,
  portal,
}: LeadModalProps) => {
  const { t } = useTranslation("people", { keyPrefix: "LEAD_MODAL" });

  const {
    lead,
    loading,
    error,
    removeLeadByIdLoading,
    updateLeadStatusByIdLoading,
    canDelete,
    handleDelete,
    handleMarkAs,
  } = useLeadModal({ leadId, onClose });

  const isRead = lead?.status === EnumLeadStatus.Read;

  return (
    <SurveyResponseModal
      isOpen={isOpen}
      onClose={onClose}
      portal={portal}
      loading={loading}
      error={error}
      accent={ACCENT}
      isNeutral={isRead}
      headerCard={
        <LeadModalHeader
          lead={lead}
          onOpenBroadcastFromLead={onOpenBroadcastFromLead}
        />
      }
      onDelete={canDelete ? handleDelete : undefined}
      deleteLoading={removeLeadByIdLoading}
      deleteLabel={t("CTA_BUTTONS.DELETE")}
      deleteDialogTitle={t("DELETE_CONFIRMATION.TITLE")}
      deleteDialogMessage={t("DELETE_CONFIRMATION.DESCRIPTION")}
      deleteDialogCancelLabel={t("CTA_BUTTONS.CANCEL")}
      primaryAction={{
        label: t(
          `CTA_BUTTONS.MARK_AS_${isRead ? "UNREAD" : "READ"}`,
        ),
        onClick: () =>
          handleMarkAs(
            isRead ? EnumLeadStatus.Unread : EnumLeadStatus.Read,
          ),
        loading: updateLeadStatusByIdLoading,
        disabled: !lead || removeLeadByIdLoading,
      }}
      closeDisabled={removeLeadByIdLoading || updateLeadStatusByIdLoading}
    >
      <ViewSubSection title={t("MESSAGE")} gap="200">
        <Text visual="bodyRegular" color="text.mid" whiteSpace="pre-wrap">
          {lead?.messages[0]?.message}
        </Text>
      </ViewSubSection>
    </SurveyResponseModal>
  );
};
